import React, { createContext, useContext, useMemo, useState } from "react";
import { createInitialState, openStayFor } from "../../mocks/carpets";

const CarpetPreviewContext = createContext(null);

export const useCarpetPreview = () => useContext(CarpetPreviewContext);

export default function CarpetPreviewProvider({ children }) {
  const [state, setState] = useState(createInitialState);

  const api = useMemo(() => ({
    state,
    reset: () => setState(createInitialState()),
    createClient: (line) => {
      const client = {
        id: `c${state.nextId}`,
        ref: String(state.nextClientRef),
        firstName: line.firstName || "",
        lastName: line.lastName || "",
        phone: line.phone || "",
        address: {
          street: line.street || "",
          city: line.city || "",
        },
      };
      setState({
        ...state,
        nextId: state.nextId + 1,
        nextClientRef: state.nextClientRef + 1,
        clients: state.clients.concat(client),
      });
      return client;
    },
    createCarpet: (line) => {
      const carpet = {
        id: `p${state.nextId}`,
        ref: String(state.nextRef),
        clientId: line.clientId,
        category: line.category,
        length: line.length,
        width: line.width,
        description: line.description || "",
        price: line.price,
      };
      setState({
        ...state,
        nextRef: state.nextRef + 1,
        nextId: state.nextId + 1,
        carpets: state.carpets.concat(carpet),
      });
      return carpet;
    },
    checkIn: (lines) => {
      let nextRef = state.nextRef;
      let nextId = state.nextId;
      const carpets = state.carpets.slice();
      const stays = state.stays.slice();
      const created = lines.map((line) => {
        let carpet = line.carpetId ? carpets.find((item) => item.id === line.carpetId) : null;
        if (!carpet) {
          carpet = {
            id: `p${nextId}`,
            ref: String(nextRef),
            clientId: line.clientId,
            category: line.category,
            length: line.length,
            width: line.width,
            description: line.description || "",
          };
          nextId += 1;
          nextRef += 1;
          carpets.push(carpet);
        }
        const stay = {
          id: `s${nextId}`,
          carpetId: carpet.id,
          clientId: carpet.clientId,
          inAt: new Date().toISOString(),
          outAt: null,
          storageBox: line.storageBox || "",
          inNotes: line.inNotes || "",
          outNotes: "",
          cleaned: !!line.cleaned,
          jobPrice: null,
        };
        nextId += 1;
        stays.push(stay);
        return stay;
      });
      const next = { ...state, nextRef, nextId, carpets, stays };
      setState(next);
      return { stays: created, carpets, clients: state.clients };
    },
    checkOut: (items) => {
      const now = new Date().toISOString();
      const byCarpet = new Map(items.map((item) => [item.carpetId, item.outNotes || ""]));
      const updated = [];
      const stays = state.stays.map((stay) => {
        if (!byCarpet.has(stay.carpetId) || stay.outAt) return stay;
        const next = { ...stay, outAt: now, outNotes: byCarpet.get(stay.carpetId) };
        updated.push(next);
        return next;
      });
      setState({ ...state, stays });
      return { stays: updated, carpets: state.carpets, clients: state.clients };
    },
    setCleaned: (stayId, cleaned) => {
      setState({
        ...state,
        stays: state.stays.map((stay) => (
          stay.id === stayId && !stay.outAt ? { ...stay, cleaned } : stay
        )),
      });
    },
    updateBoxes: (items) => {
      const byId = new Map(items.map((item) => [item.stayId, item.storageBox]));
      setState({
        ...state,
        stays: state.stays.map((stay) => (
          byId.has(stay.id) && !stay.outAt
            ? { ...stay, storageBox: byId.get(stay.id) }
            : stay
        )),
      });
    },
    openStayFor: (carpetId) => openStayFor(state, carpetId),
  }), [state]);

  return (
    <CarpetPreviewContext.Provider value={api}>
      {children}
    </CarpetPreviewContext.Provider>
  );
}
